import type { CSSProperties } from 'react';
import { DS2 } from '../../theme/designTokens';

/**
 * Settings V1 — shared DS2 volume slider (Figma Settings "Sound Volume").
 *
 * A styled NATIVE <input type="range"> — keyboard arrows, Home/End, focus
 * and screen-reader semantics come from the platform, not a re-implemented
 * widget. Controlled only; the caller owns state, persistence, and the
 * visible percentage readout (pass `ariaLabel` so the control names itself).
 *
 * Figma anatomy: thin dark track, gold-gradient circular thumb with an
 * outer gold ring. The gradient fill left of the thumb uses the same DS2
 * gold as every approved control.
 */
export interface VolumeSliderProps {
  /** 0–100 integer. */
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  ariaLabel: string;
  id?: string;
  style?: CSSProperties;
}

export function VolumeSlider({ value, onChange, disabled = false, ariaLabel, id, style }: VolumeSliderProps) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <>
      <style>{`
        .ds2-volume-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 6px;
          border-radius: 3px;
          outline: none;
          cursor: pointer;
        }
        .ds2-volume-slider:disabled { cursor: not-allowed; opacity: 0.45; }
        .ds2-volume-slider:focus-visible { outline: 2px solid ${DS2.color.gold}; outline-offset: 4px; }
        .ds2-volume-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: ${DS2.gradient.gold};
          border: 1px solid ${DS2.color.gold};
          box-shadow: 0 0 0 5px rgba(11,11,11,0.9), 0 0 0 6px ${DS2.color.gold};
        }
        .ds2-volume-slider::-moz-range-thumb {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: ${DS2.gradient.gold};
          border: 1px solid ${DS2.color.gold};
          box-shadow: 0 0 0 5px rgba(11,11,11,0.9), 0 0 0 6px ${DS2.color.gold};
        }
      `}</style>
      <input
        type="range"
        id={id}
        className="ds2-volume-slider"
        min={0}
        max={100}
        step={1}
        value={pct}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-valuetext={`${pct}%`}
        onChange={e => onChange(Number(e.currentTarget.value))}
        style={{
          // Gold fill to the left of the thumb, dark rail to the right.
          background: `linear-gradient(to right, ${DS2.color.gold} 0%, ${DS2.color.gold} ${pct}%, #4D4D4D ${pct}%, #4D4D4D 100%)`,
          ...style,
        }}
      />
    </>
  );
}
